import { Head, Link, router, useForm, usePage } from '@inertiajs/react';
import { useRef, useState, type FormEvent } from 'react';

import { AutoTextarea } from '@/components/admin/forms/AutoTextarea';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { trackUmamiEvent } from '@/lib/analytics';

type EventOption = {
  id: number;
  title: string;
  startsAt: string | null;
};

type GalleryItem = {
  id: number;
  mediaType: 'image' | 'video';
  caption: string | null;
  altText: string | null;
  position: number;
  cover: boolean;
  mediaUrl: string | null;
};

type Gallery = {
  id: number;
  title: string;
  slug: string;
  description: string | null;
  publishedAt: string | null;
  eventId: number;
  eventTitle: string;
  itemCount: number;
  coverUrl: string | null;
  items: GalleryItem[];
};

type UploadRow = {
  id: string;
  file: File;
  status: 'uploading' | 'error';
  progress: number;
  error?: string;
};

type Props = {
  gallery: Gallery | null;
  events: EventOption[];
  [key: string]: unknown;
};

const labelClass = 'text-on-surface mb-1.5 block text-sm font-medium';
const inputClass =
  'bg-surface border-line text-on-surface focus:border-primary w-full rounded-md border px-3 py-2 text-sm outline-none transition-all focus:shadow-[0_0_0_3px_rgb(from_var(--color-primary)_r_g_b/0.12)]';
const STATUS_LABELS: Record<UploadRow['status'], string> = {
  uploading: 'A enviar',
  error: 'Erro',
};

function fileMediaType(file: File): 'image' | 'video' {
  return file.type.startsWith('video/') ? 'video' : 'image';
}

function localDateTime(): string {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
}

export default function GalleryEditor() {
  const { gallery, events } = usePage<Props>().props;
  const isEdit = Boolean(gallery);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const nextUploadIdRef = useRef(0);
  const [queue, setQueue] = useState<UploadRow[]>([]);

  const form = useForm({
    gallery: {
      event_id: gallery?.eventId ? String(gallery.eventId) : '',
      title: gallery?.title ?? '',
      description: gallery?.description ?? '',
      published_at: gallery?.publishedAt ? gallery.publishedAt.slice(0, 16) : localDateTime(),
    },
  });
  const { data, setData, processing } = form;
  const errors = form.errors as Record<string, string | undefined>;

  function setField(key: keyof typeof data.gallery, value: string) {
    setData('gallery', { ...data.gallery, [key]: value });
  }

  function submit(ev: FormEvent) {
    ev.preventDefault();
    trackUmamiEvent(gallery ? 'admin_content_update' : 'admin_content_create', {
      content_type: 'gallery',
    });
    if (gallery) form.patch(`/galleries/${gallery.id}`);
    else form.post('/galleries');
  }

  function addFiles(files: FileList | null) {
    if (!gallery || !files) return;
    const rows = Array.from(files).map((file) => ({
      id: `${Date.now()}-${nextUploadIdRef.current++}-${file.name}-${file.size}`,
      file,
      status: 'uploading' as const,
      progress: 0,
    }));
    setQueue((current) => [...current, ...rows]);
    rows.forEach((row) => uploadFile(row));
  }

  function setQueueRow(id: string, patch: Partial<UploadRow>) {
    setQueue((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  function uploadFile(row: UploadRow) {
    if (!gallery) return;

    const payload = new FormData();
    payload.append('gallery_item[media_type]', fileMediaType(row.file));
    payload.append('gallery_item[alt_text]', row.file.name.replace(/\.[^.]+$/, ''));
    payload.append('media', row.file);

    router.post(`/galleries/${gallery.id}/items`, payload, {
      forceFormData: true,
      preserveScroll: true,
      onProgress: (progress) => setQueueRow(row.id, { progress: progress?.percentage ?? 0 }),
      onSuccess: () => setQueue((current) => current.filter((item) => item.id !== row.id)),
      onError: () =>
        setQueueRow(row.id, {
          status: 'error',
          progress: 0,
          error: 'Falhou o envio. Verifique o MinIO e tente novamente.',
        }),
    });
  }

  function updateItem(item: GalleryItem, patch: Partial<GalleryItem>) {
    router.patch(
      `/galleries/${gallery?.id}/items/${item.id}`,
      {
        gallery_item: {
          caption: patch.caption ?? item.caption ?? '',
          alt_text: patch.altText ?? item.altText ?? '',
          position: patch.position ?? item.position,
        },
      },
      { preserveScroll: true },
    );
  }

  function deleteItem(item: GalleryItem) {
    if (!gallery || !window.confirm('Remover este item da galeria?')) return;
    router.delete(`/galleries/${gallery.id}/items/${item.id}`, { preserveScroll: true });
  }

  function setCover(item: GalleryItem) {
    if (!gallery) return;
    router.patch(`/galleries/${gallery.id}/items/${item.id}/cover`, {}, { preserveScroll: true });
  }

  const eventOptions = [
    { label: 'Selecionar evento', value: '' },
    ...events.map((event) => ({ label: event.title, value: String(event.id) })),
  ];

  return (
    <>
      <Head title={isEdit ? 'Editar galeria — Administração' : 'Nova galeria — Administração'} />

      <div className="space-y-5">
        <form onSubmit={submit}>
          <div className="border-line-strong bg-surface flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 shadow-[0_14px_34px_-28px_rgba(33,29,24,0.5)]">
            <Link
              href="/galleries"
              className="text-on-surface-light hover:text-primary inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">arrow_back</span>
              Galerias
            </Link>
            <div className="border-line flex flex-col border-l pl-3.5 leading-tight">
              <span className="text-secondary text-[10px] font-bold tracking-[0.12em] uppercase">
                {isEdit ? 'Editar galeria' : 'Nova galeria'}
              </span>
              <span className="font-headline text-on-surface max-w-[40ch] truncate text-base font-semibold">
                {data.gallery.title.trim() || 'Sem título'}
              </span>
            </div>
            <div className="ml-auto flex items-center gap-2.5">
              <Link
                href="/galleries"
                className="bg-surface border-line-strong text-on-surface hover:border-primary hover:text-primary inline-flex items-center justify-center gap-2 rounded-md border px-4 py-2 text-sm font-semibold transition-colors duration-200"
              >
                Cancelar
              </Link>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={processing}
                loading={processing}
              >
                {!processing && <span className="material-symbols-outlined text-[18px]">save</span>}
                {processing ? 'A guardar…' : 'Guardar'}
              </Button>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 items-start gap-5 lg:grid-cols-[1fr_320px]">
            <div className="space-y-4">
              <div className="surface-card px-5 py-4">
                <input
                  type="text"
                  value={data.gallery.title}
                  onChange={(ev) => setField('title', ev.target.value)}
                  required
                  placeholder="Título da galeria"
                  aria-label="Título"
                  className="font-headline text-on-surface placeholder:text-on-surface-light/40 w-full border-0 bg-transparent text-2xl leading-tight font-semibold outline-none sm:text-3xl"
                />
                {errors.title && <p className="text-error mt-1.5 text-xs">{errors.title}</p>}
              </div>

              <div className="surface-card px-5 py-4">
                <label className={labelClass} htmlFor="gallery-description">
                  Descrição
                </label>
                <AutoTextarea
                  id="gallery-description"
                  value={data.gallery.description}
                  onChange={(ev) => setField('description', ev.target.value)}
                  rows={5}
                  placeholder="Texto curto para contextualizar a galeria…"
                />
              </div>
            </div>

            <aside className="space-y-4 lg:sticky lg:top-4">
              <div className="surface-card space-y-4 px-4 py-4">
                <div>
                  <span className={labelClass}>Evento associado</span>
                  <Select
                    options={eventOptions}
                    value={data.gallery.event_id}
                    onChange={(value) => setField('event_id', value)}
                    className="w-full"
                  />
                  {errors.event && <p className="text-error mt-1 text-xs">{errors.event}</p>}
                  {errors.event_id && <p className="text-error mt-1 text-xs">{errors.event_id}</p>}
                </div>
                <div>
                  <label className={labelClass} htmlFor="gallery-published">
                    Publicação
                  </label>
                  <input
                    id="gallery-published"
                    type="datetime-local"
                    value={data.gallery.published_at}
                    onChange={(ev) => setField('published_at', ev.target.value)}
                    required
                    className={inputClass}
                  />
                </div>
              </div>
            </aside>
          </div>
        </form>

        {gallery && (
          <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[360px_1fr]">
            <section className="surface-card space-y-4 p-4">
              <div>
                <h2 className="font-headline text-on-surface text-xl font-semibold">
                  Adicionar media
                </h2>
                <p className="text-on-surface-light mt-1 text-sm">
                  Selecione muitos ficheiros de uma vez. O envio é feito um a um para reduzir
                  falhas.
                </p>
              </div>

              <div
                onDragOver={(ev) => ev.preventDefault()}
                onDrop={(ev) => {
                  ev.preventDefault();
                  addFiles(ev.dataTransfer.files);
                }}
                className="border-line-strong bg-background flex flex-col items-center gap-3 rounded-lg border border-dashed px-4 py-8 text-center"
              >
                <span className="material-symbols-outlined text-primary text-4xl">
                  cloud_upload
                </span>
                <div>
                  <p className="text-on-surface text-sm font-semibold">
                    Arraste fotografias ou vídeos
                  </p>
                  <p className="text-on-surface-light text-xs">JPG, PNG, WebP, MP4 ou WebM</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp,video/mp4,video/webm"
                  className="hidden"
                  onChange={(ev) => {
                    addFiles(ev.target.files);
                    ev.currentTarget.value = '';
                  }}
                />
                <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
                  Escolher ficheiros
                </Button>
              </div>

              {queue.length > 0 && (
                <div className="space-y-3">
                  <p className="text-on-surface-light text-xs">
                    {queue.filter((row) => row.status === 'uploading').length} em envio
                    {queue.some((row) => row.status === 'error') && ' · existem falhas'}
                  </p>
                  <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                    {queue.map((row) => (
                      <div
                        key={row.id}
                        className="border-line bg-surface space-y-2 rounded-md border px-3 py-2 text-xs"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-on-surface truncate">{row.file.name}</span>
                          <span className="text-on-surface-light shrink-0">
                            {row.status === 'uploading'
                              ? `${STATUS_LABELS[row.status]} ${Math.round(row.progress)}%`
                              : STATUS_LABELS[row.status]}
                          </span>
                        </div>
                        {row.status === 'uploading' && (
                          <div className="bg-line h-1.5 overflow-hidden rounded-full">
                            <div
                              className="bg-primary h-full rounded-full transition-all duration-200"
                              style={{ width: `${row.progress}%` }}
                            />
                          </div>
                        )}
                        {row.error && <p className="text-error mt-1">{row.error}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <section className="surface-card p-4">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-headline text-on-surface text-xl font-semibold">
                  Itens da galeria
                </h2>
                <span className="text-on-surface-light text-sm">{gallery.items.length} itens</span>
              </div>
              {gallery.items.length === 0 ? (
                <p className="text-on-surface-light py-8 text-center text-sm">
                  Ainda não existem itens.
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {gallery.items.map((item) => (
                    <article
                      key={item.id}
                      className="border-line bg-background overflow-hidden rounded-lg border"
                    >
                      <div className="bg-surface aspect-[4/3]">
                        {item.mediaType === 'image' && item.mediaUrl ? (
                          <img
                            src={item.mediaUrl}
                            alt=""
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : item.mediaType === 'video' && item.mediaUrl ? (
                          <video
                            src={item.mediaUrl}
                            className="h-full w-full object-cover"
                            controls
                          >
                            <track kind="captions" />
                          </video>
                        ) : null}
                      </div>
                      <div className="space-y-2 p-3">
                        <input
                          type="text"
                          defaultValue={item.caption ?? ''}
                          onBlur={(ev) => updateItem(item, { caption: ev.target.value })}
                          placeholder="Legenda"
                          className={inputClass}
                        />
                        <input
                          type="text"
                          defaultValue={item.altText ?? ''}
                          onBlur={(ev) => updateItem(item, { altText: ev.target.value })}
                          placeholder="Texto alternativo"
                          className={inputClass}
                        />
                        <div className="flex items-center justify-between gap-2 pt-1">
                          <Button
                            variant={item.cover ? 'green' : 'secondary'}
                            size="sm"
                            onClick={() => setCover(item)}
                            disabled={item.cover}
                          >
                            {item.cover ? 'Capa' : 'Definir capa'}
                          </Button>
                          <button
                            type="button"
                            onClick={() => deleteItem(item)}
                            className="text-on-surface-light hover:bg-error/10 hover:text-error rounded-md p-1.5 transition-colors"
                            aria-label="Eliminar item"
                          >
                            <span className="material-symbols-outlined text-[18px]">delete</span>
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </>
  );
}
